/**
 * TestKitsPage — top-level page for the `/test-kits` route.
 *
 * Manages tab state ('kits' | 'quick' | 'logs'), selected test-kit state, log
 * persistence, and hosts the modal overlay for the step walker.
 * Imports the print stylesheet for bonus D.
 */
import { useState, useCallback, useEffect } from 'react';
import { TEST_KITS_CATALOG } from '@reefapp/shared';
import type { TestKit, TestLogEntry } from '@reefapp/shared';
import { useTestKitLogs } from '../test-kits/useTestKitLogs';
import { useReminderSettings } from '../test-kits/useReminderSettings';
import { useReagentDates } from '../test-kits/useReagentDates';
import { TestSelector } from '../test-kits/TestSelector';
import { TestRunnerModal } from '../test-kits/TestRunnerModal';
import { LogViewer } from '../test-kits/LogViewer';
import { ReminderSettingsPanel } from '../test-kits/ReminderSettingsPanel';
import { add as addManualReading } from '../manual-readings/ManualReadingStore';
import { ensureParameterLogSync } from '../manual-readings/readingSync';
import { slugify } from '@reefapp/shared';
import { GuidedLogCard } from '../test-kits/GuidedLog/GuidedLogCard';
import '../test-kits/print.css';

/* ---------- Inline SVG icon (matches standalone visual) --------------- */
function BeakerHeaderIcon() {
  return (
    <svg
      aria-hidden="true"
      width="48"
      height="48"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="text-cyan-400"
    >
      <path d="M8 2h8v3l4 10H4L8 5V2z" />
      <path d="M8 2v3" />
      <line x1="9" y1="11" x2="15" y2="11" />
      <line x1="10" y1="14" x2="14" y2="14" />
    </svg>
  );
}

const ACTIVE_TAB_KEY = 'reefapp.testKits.activeTab';

type TabId = 'kits' | 'quick' | 'logs';

function readInitialTab(): TabId {
  try {
    const stored = window.localStorage.getItem(ACTIVE_TAB_KEY);
    if (stored === 'logs') return 'logs';
    if (stored === 'quick') return 'quick';
  } catch {
    // Storage failure — use default
  }
  return 'kits';
}

export function TestKitsPage() {
  const { logs, addLog, deleteLog, setLogs } = useTestKitLogs();
  const { settings, setEnabled, setThresholdDays } = useReminderSettings();
  const { dates, setDate, clearDate, monthsSince } = useReagentDates();

  const [activeTab, setActiveTab] = useState<TabId>(readInitialTab);
  const [selectedKit, setSelectedKit] = useState<TestKit | null>(null);

  // Mirror manual readings to the Pi (this page dual-writes readings).
  useEffect(() => {
    ensureParameterLogSync();
  }, []);

  // Persist tab choice
  useEffect(() => {
    try {
      window.localStorage.setItem(ACTIVE_TAB_KEY, activeTab);
    } catch {
      // ignore storage errors
    }
  }, [activeTab]);

  const handleSelectTest = useCallback((kit: TestKit) => {
    setSelectedKit(kit);
  }, []);

  const handleCloseModal = useCallback(() => {
    setSelectedKit(null);
  }, []);

  const handleLog = useCallback(
    (entry: Omit<TestLogEntry, 'id' | 'date'>) => {
      addLog(entry);
      setSelectedKit(null);

      // Phase 4: also write to ManualReadingStore (IndexedDB)
      if (typeof entry.result === 'number') {
        void addManualReading({
          kitId: `${slugify(entry.brand)}-${slugify(entry.parameter)}`,
          brand: entry.brand,
          parameter: entry.parameter,
          value: entry.result,
          unit: entry.unit,
          notes: entry.notes,
        });
      }
    },
    [addLog],
  );

  const handleDeleteLog = useCallback(
    (id: string) => {
      if (window.confirm('Are you sure you want to delete this log entry?')) {
        deleteLog(id);
      }
    },
    [deleteLog],
  );

  const handleSetReagentDate = useCallback(
    (slug: string, iso: string | null) => {
      if (iso === null) {
        clearDate(slug);
      } else {
        setDate(slug, iso);
      }
    },
    [setDate, clearDate],
  );

  return (
    <div className="reefapp-test-kits-scope min-h-screen font-sans p-4 sm:p-6 lg:p-8 bg-slate-950">
      <div className="max-w-7xl mx-auto">
        {/* Header banner — matches standalone visual */}
        <header className="bg-slate-800 p-6 rounded-2xl border border-slate-700 shadow-lg text-center mb-8">
          <div className="flex justify-center items-center gap-4">
            <BeakerHeaderIcon />
            <h1 className="text-4xl sm:text-5xl font-bold text-cyan-300">
              Aquarium Test Kit Assistant
            </h1>
          </div>
          <p className="mt-2 text-lg text-slate-300 max-w-3xl mx-auto">
            Your interactive guide to aquarium water testing. Follow
            step-by-step instructions, log parameters, and get dosing
            recommendations.
          </p>
        </header>

        {/* Tab toggle — segmented control */}
        <div className="flex justify-center mb-8">
          <div className="bg-slate-800 rounded-lg p-1 inline-flex">
            <button
              onClick={() => setActiveTab('kits')}
              className={`px-6 py-2 rounded-md text-sm font-semibold transition-colors ${
                activeTab === 'kits'
                  ? 'bg-cyan-600 text-white shadow'
                  : 'text-slate-300 hover:bg-slate-700'
              }`}
            >
              Test Kits
            </button>
            <button
              onClick={() => setActiveTab('quick')}
              className={`px-6 py-2 rounded-md text-sm font-semibold transition-colors ${
                activeTab === 'quick'
                  ? 'bg-cyan-600 text-white shadow'
                  : 'text-slate-300 hover:bg-slate-700'
              }`}
            >
              Quick Log
            </button>
            <button
              onClick={() => setActiveTab('logs')}
              className={`px-6 py-2 rounded-md text-sm font-semibold transition-colors ${
                activeTab === 'logs'
                  ? 'bg-cyan-600 text-white shadow'
                  : 'text-slate-300 hover:bg-slate-700'
              }`}
            >
              Logs & Plans
            </button>
          </div>
        </div>

        {/* Content area */}
        {activeTab === 'kits' && (
          <TestSelector
            testKits={TEST_KITS_CATALOG}
            logs={logs}
            reminderSettings={settings}
            reagentDates={dates}
            monthsSince={monthsSince}
            onSelectTest={handleSelectTest}
            onSetReagentDate={handleSetReagentDate}
          />
        )}
        {activeTab === 'quick' && <GuidedLogCard />}
        {activeTab === 'logs' && (
          <div className="space-y-6">
            <LogViewer
              logs={logs}
              onDeleteLog={handleDeleteLog}
              onImport={setLogs}
            />
            <ReminderSettingsPanel
              settings={settings}
              onSetEnabled={setEnabled}
              onSetThresholdDays={setThresholdDays}
            />
          </div>
        )}

        {/* Modal overlay */}
        {selectedKit && (
          <TestRunnerModal
            testKit={selectedKit}
            logs={logs}
            onClose={handleCloseModal}
            onLog={handleLog}
          />
        )}

        {/* Footer disclaimer */}
        <footer className="mt-12 text-center text-sm">
          <p className="text-slate-500">
            Copyright &copy; {new Date().getFullYear()} by Josh Cole
          </p>
        </footer>
      </div>
    </div>
  );
}
